import React, { useState, useEffect } from "react";
import Button from "./Button";
import DateUtils from "../../../utils/dateUtils";
import Confirmation from "./Confirmation";
import CommonModal from "../Modal/CommonModal";
import GameSettingUpdateModal from "../Modal/Report/GameSettingUpdateModal";

const InputTable = ({
  tableData = [],
  onUpdate,
  showUpdateAll = true,
  canUpdate = true,
  handleModal,
}) => {
  const [rows, setRows] = useState([]);
  const [confirmationState, setConfirmationState] = useState({
    isOpen: false,
    row: null,
    updateAll: false,
  });
  const [isConfirmed, setIsConfirmed] = useState(true);
    const [isShow, setIsShow] = useState({
      isOpen: false,
      rowData: null,
      modalTitle: "",
      modalContent: null,
      modalSize: "sm",
    })
  const hiddenFields = [
    "marketTypeSettingId",
    "marketTypeId",
    "bettingTypeSettingId",
    "bettingTypeId",
    "competitionId",
    "competitionSettingId",
    "actionBy",
    "eventId",
    "eventSettingId",
    "marketId",
    "marketSettingId",
    "actionOn",
    "Id",
    "eventTypeName",
  ];

  useEffect(() => {
    setRows(Array.isArray(tableData) ? tableData : []);
  }, [tableData]);

  // Convert header into sentence case
  const formatHeader = (text) => {
    if (text?.toLowerCase() === "id") return "ID";

    return text
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (str) => str.toUpperCase())
      .trim();
  };

  // Check if field should behave like a numeric input
  const isNumericField = (key, value) => {
    if (
      key?.toLowerCase() === "id" ||
      isReadOnlyField(key) ||
      isDateField(key, value)
    ) {
      return false;
    }

    return typeof value === "number";
  };

  // Handle input change
  const handleChange = (rowIndex, key, value) => {
    const updatedRows = [...rows];

    const sourceValue = tableData?.[rowIndex]?.[key];

    // Allow only positive numbers and decimals for numeric fields
    if (isNumericField(key, sourceValue)) {
      if (!/^\d*\.?\d*$/.test(value)) {
        return;
      }
    }

    updatedRows[rowIndex] = {
      ...updatedRows[rowIndex],
      [key]: value,
    };

    setRows(updatedRows);
  };

  const isDateField = (key, value) => {
    if (!value || typeof value !== "string") return false;

    const normalizedKey = key.toLowerCase();
    const looksLikeDateKey =
      normalizedKey.includes("date") || normalizedKey.endsWith("on");
    const looksLikeUtcValue =
      value.includes("T") &&
      (value.endsWith("Z") || value.includes("+") || value.includes("-"));

    return looksLikeDateKey && looksLikeUtcValue;
  };

  const formatCellValue = (key, value) => {
    if (isDateField(key, value)) {
      return DateUtils.fromUTC(value);
    }

    return value ?? "";
  };

  // Handle row update
  const handleUpdate = (row, updateAll = false) => {
    const { actionOn, actionBy, bettingTypeName, ...filteredRow } = row;
    console.log(" Final Updated Row:", filteredRow);

    // replace empty string with 0
    const updatedRow = Object.fromEntries(
      Object.entries(filteredRow).map(([key, value]) => [
        key,
        value === "" ? 0 : value,
      ]),
    );

    const finalRow = showUpdateAll
      ? {
          ...updatedRow,
          updateAll,
        }
      : updatedRow;

    if (onUpdate) {
      onUpdate(finalRow);
    }
  };

  // const handleOpenConfirmation = (row, updateAll = false) => {
  //   setConfirmationState({
  //     isOpen: true,
  //     row,
  //     updateAll,
  //   });
  // };

  // const handleCloseConfirmation = () => {
  //   setConfirmationState({
  //     isOpen: false,
  //     row: null,
  //     updateAll: false,
  //   });
  // };

  // const handleConfirmUpdate = async () => {
  //   if (!confirmationState.row) return;

  //   await handleUpdate(confirmationState.row, confirmationState.updateAll);
  //   handleCloseConfirmation();
  // };

  // Dynamic headers
  const headers =
    rows.length > 0
      ? Object.keys(rows[0]).filter((header) => !hiddenFields.includes(header))
      : [];

  const getColumnClassName = (key) => {
    const normalizedKey = key.toLowerCase();

    if (
      normalizedKey === "actionon" ||
      normalizedKey.includes("date") ||
      normalizedKey.endsWith("on")
    ) {
      return "input-table__cell input-table__cell-date";
    }

    if (
      normalizedKey.includes("name") ||
      normalizedKey.includes("title") ||
      normalizedKey.includes("label")
    ) {
      return "input-table__cell input-table__cell--name";
    }

    if (normalizedKey === "id") {
      return "input-table__cell input-table__cell-date";
    }

    return "input-table__cell ";
  };

  // Name field should be read only
  const isReadOnlyField = (key) => {
    const normalizedKey = key.toLowerCase();
    return (
      normalizedKey.includes("name") ||
      normalizedKey.includes("label") ||
      normalizedKey.includes("title")
    );
  };

  const ModalComponent = isShow.modalContent;

  return (
    <div className="table-responsive">
      <table className="report-card-box input-table">
        <thead>
          <tr>
            {headers.map((header, index) => (
              <th key={index} className={getColumnClassName(header)}>
                {formatHeader(header)}
              </th>
            ))}

            {headers.length > 0 && canUpdate && (
              <th className="input-table__action">Action</th>
            )}
          </tr>
        </thead>

        <tbody>
          {rows.map((row, rowIndex) => {
            return (
              <tr key={rowIndex}>
                {headers.map((key, colIndex) => (
                  <td key={colIndex} className={getColumnClassName(key)}>
                    {key.toLowerCase() === "id" ? (
                      <span>{row[key]}</span>
                    ) : colIndex === 0 ? (
                      <span title={formatCellValue(key, row[key])}>
                        {formatCellValue(key, row[key])}
                      </span>
                    ) : (
                      <input
                        type="text"
                        inputMode={
                          isNumericField(key, tableData?.[rowIndex]?.[key])
                            ? "decimal"
                            : "text"
                        }
                        value={formatCellValue(key, row[key])}
                        onChange={(e) =>
                          handleChange(rowIndex, key, e.target.value)
                        }
                        title={String(formatCellValue(key, row[key]))}
                        className="form-control"
                        readOnly={isReadOnlyField(key)}
                      />
                    )}
                  </td>
                ))}
                {headers.length > 0 && canUpdate && (
                  <td className="text-center ">
                    <div className="input-table__action d-flex gap-1 justify-content-center">
                      <Button
                        className="btn-prev "
                        onClick={() =>
                          handleModal({
                            isOpen: true,
                            rowData: row,
                            updateAll: false,
                            name: "",
                            modalTitle: "Update Setting",
                            modalContent: GameSettingUpdateModal,
                            apiFlag: false,
                            handleUpdate: handleUpdate,
                            modalSize: "sm",
                          })
                        }
                      >
                        Update
                      </Button>
                      {showUpdateAll && (
                        <Button
                          className="btn btn_main"
                          onClick={() =>
                            handleModal({
                              isOpen: true,
                              rowData: row,
                              updateAll: true,
                              modalTitle: "Update All Setting",
                              modalContent: GameSettingUpdateModal,
                              handleUpdate: handleUpdate,
                              modalSize: "sm",
                            })
                          }
                        >
                          Update All
                        </Button>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* {confirmationState.isOpen && (
        <CommonModal
          isShow={confirmationState.isOpen}
          handleModalHide={handleCloseConfirmation}
          modalTitle={
            confirmationState.updateAll
              ? "Confirm Update All Setting"
              : "Confirm Update Setting"
          }
          isFooter={false}
          size="md"
        >
          <Confirmation
            onConfirm={handleConfirmUpdate}
            onCancel={handleCloseConfirmation}
            title="Confirmation"
            confirmText="Submit"
            description="Enter your password to continue with this update."
            isConfirmed={isConfirmed}
          />
        </CommonModal>
      )} */}

      {isShow.isOpen && (
        <CommonModal
          isShow={isShow.isOpen}
          handleModalHide={() =>
            setIsShow((prev) => ({
              ...prev,
              isOpen: false,
            }))
          }
          modalTitle={isShow.modalTitle || "Game Setting"}
          isFooter={false}
          size={isShow.modalSize}
        >
          {ModalComponent && (
            <ModalComponent rowData={isShow.rowData} selectedKey={activeKey} />
          )}
        </CommonModal>
      )}
    </div>
  );
};

export default InputTable;
